import { RutgersCourse, UserAPCredit } from '@/app/types/interfaces';
import { equivalentCourseCodes } from '@/app/lib/courseEquivalencies';

export interface PrerequisiteCheck {
  course: RutgersCourse;
  isSatisfied: boolean;
  missingPrerequisites: string[];
  satisfiedPrerequisites: string[];
  prerequisites?: string;
  error?: string;
}

export interface PrerequisiteRequirement {
  type: 'course' | 'credit' | 'grade' | 'department';
  value: string;
  operator?: 'AND' | 'OR' | 'GREATER_THAN' | 'EQUAL';
}

// Cache for prerequisite parsing and checking
const prerequisiteParseCache = new Map<string, PrerequisiteRequirement[]>();
const prerequisiteCheckCache = new Map<string, PrerequisiteCheck>();
const PREREQUISITE_DEBUG = process.env.NEXT_PUBLIC_DEBUG_PREREQUISITES === 'true';

function debugPrerequisites(...args: unknown[]) {
  if (PREREQUISITE_DEBUG) {
    console.debug(...args);
  }
}

function normalizeCourseIdentity(course: Pick<RutgersCourse, 'baseId' | 'id' | 'department' | 'class_number' | 'section'>): string {
  const baseId = String(course.baseId || course.id || '').replace(/[^0-9]/g, '');
  const department = String(course.department || '').replace(/[^0-9]/g, '');
  const classNumber = String(course.class_number || '').replace(/[^0-9]/g, '');
  const fallback = department && classNumber ? `${department}${classNumber}` : '';
  return baseId || fallback || String(course.id || course.section || '').trim();
}

function buildPrerequisiteCheckCacheKey(
  course: RutgersCourse,
  completedCourses: RutgersCourse[],
  apCredits: UserAPCredit[]
): string {
  const target = normalizeCourseIdentity(course);
  const completedIdentity = completedCourses
    .map(normalizeCourseIdentity)
    .filter(Boolean)
    .sort()
    .join(',');
  const apIdentity = apCredits
    .filter(apCredit => apCredit.isApplied)
    .flatMap(apCredit => apCredit.rutgersEquivalents || [])
    .map(equivalent => `${equivalent.rutgersCourse || ''}:${equivalent.credits || ''}`)
    .filter(Boolean)
    .sort()
    .join(',');

  return [
    target,
    course.prerequisites || '',
    completedIdentity,
    apIdentity,
  ].join('|');
}

// Clear caches periodically to prevent memory leaks
setInterval(() => {
  prerequisiteParseCache.clear();
  prerequisiteCheckCache.clear();
}, 15 * 60 * 1000); // Clear every 15 minutes

/**
 * Converts AP credits to Rutgers course equivalents
 */
export function convertAPCreditsToCourses(apCredits: UserAPCredit[]): RutgersCourse[] {
  const apCourseEquivalents: RutgersCourse[] = [];
  
  apCredits.forEach(apCredit => {
    if (!apCredit.isApplied) return;
    
    apCredit.rutgersEquivalents.forEach(equivalent => {
      // Skip if rutgersCourse is undefined or not a valid course code
      if (!equivalent.rutgersCourse || equivalent.rutgersCourse === 'ELECTIVE') {
        return;
      }
      
      // Create a RutgersCourse object from the AP credit equivalent
      const courseCodeParts = equivalent.rutgersCourse.split(':');
      if (courseCodeParts.length !== 3) {
        console.warn('Invalid course code format:', equivalent.rutgersCourse);
        return;
      }
      
      const [school, subject, number] = courseCodeParts;
      const department = `${school}:${subject}`; // match site-wide formatting
      const classNumber = number;
      const normalizedBaseId = `${school}${subject}${number}`; // baseId without colons
      
      const apCourse: RutgersCourse = {
        baseId: normalizedBaseId,
        id: normalizedBaseId,
        courseName: equivalent.courseName,
        fullTitle: equivalent.courseName,
        department,
        class_number: classNumber,
        credits: equivalent.credits,
        campus: 'NB', // Default campus
        meetingTimes: [],
        instructors: [],
        section: 'AP',
        subjectNotes: `AP Credit from ${apCredit.examName} (Score: ${apCredit.score}/5)`,
        coreRequirements: [],
        prerequisites: undefined
      };
      
      apCourseEquivalents.push(apCourse);
    });
  });
  
  return apCourseEquivalents;
}

/**
 * Parses prerequisite text into structured requirements with caching
 */
export function parsePrerequisites(prereqText: string): PrerequisiteRequirement[] {
  if (!prereqText) return [];
  
  // Check cache first
  if (prerequisiteParseCache.has(prereqText)) {
    return prerequisiteParseCache.get(prereqText)!;
  }
  
  const requirements: PrerequisiteRequirement[] = [];
  
  // Remove HTML tags and clean up the text
  const cleanText = prereqText
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  
  // Extract course codes (e.g., "01:198:111", "01:640:251")
  const coursePattern = /\((\d{2}:\d{3}:\d{3})\)/g;
  let match;
  
  while ((match = coursePattern.exec(cleanText)) !== null) {
    requirements.push({
      type: 'course',
      value: match[1],
      operator: 'AND' // Default to AND
    });
  }
  
  // Extract credit requirements (e.g., "Any Course EQUAL or GREATER Than: (01:640:112)")
  const creditPattern = /Any Course EQUAL or GREATER Than: \(([^)]+)\)/g;
  while ((match = creditPattern.exec(cleanText)) !== null) {
    requirements.push({
      type: 'credit',
      value: match[1],
      operator: 'GREATER_THAN'
    });
  }
  
  // Cache the result
  prerequisiteParseCache.set(prereqText, requirements);
  return requirements;
}

/**
 * Parses prerequisite text into disjunctive normal form: an array of OR groups,
 * where each group is an array of course codes that must all be taken (AND).
 */
// Parse prerequisites into DNF: array of OR groups; each group is an array of clauses;
// each clause is an array of acceptable course codes (OR within the clause).
// Example: ((A or B) and (C)) OR ((D or E) and (C)) -> [ [ [A,B], [C] ], [ [D,E], [C] ] ]
export function parsePrerequisiteGroups(prereqText: string): string[][][] {
  if (!prereqText) return [];
  const text = prereqText
    .replace(/<[^>]*>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  // Split at top-level ORs (not inside parentheses)
  const groups: string[] = [];
  let depth = 0;
  let buffer = '';
  for (let i = 0; i < text.length; i++) {
    const c = text[i];
    if (c === '(') depth++;
    if (c === ')') depth = Math.max(0, depth - 1);
    // Check for ' OR ' at depth 0
    // "Any Course EQUAL or GREATER Than: (...)" is one clause, not two alternatives.
    const isEqualOrGreater = /EQUAL$/i.test(buffer) && /^ OR GREATER/i.test(text.slice(i, i + 12));
    if (depth === 0 && !isEqualOrGreater && text.slice(i, i + 4).toUpperCase() === ' OR ') {
      groups.push(buffer.trim());
      buffer = '';
      i += 3; // skip ' OR'
      continue;
    }
    buffer += c;
  }
  if (buffer.trim()) groups.push(buffer.trim());

  const result: string[][][] = [];
  const toCodes = (s: string): string[] => s.match(/\d{2}:\d{3}:\d{3}/g) || [];

  for (const g of groups) {
    // For each group, collect clauses as content inside parentheses.
    // Each clause can contain multiple codes (interpreted as OR).
    const clauses: string[][] = [];
    const parenRegex = /\(([^)]*)\)/g;
    let m: RegExpExecArray | null;
    while ((m = parenRegex.exec(g)) !== null) {
      const codes = toCodes(m[1]);
      if (codes.length > 0) {
        // Detect context like "Any Course EQUAL or GREATER Than: (01:640:112)"
        const contextStart = Math.max(0, m.index - 120);
        const context = g.slice(contextStart, m.index);
        const isGreater = /EQUAL\s*or\s*GREATER\s*Than/i.test(context);
        clauses.push(isGreater ? codes.map(c => `>=${c}`) : codes);
      }
    }
    if (clauses.length === 0) {
      // Fallback: treat all codes in group as a single clause
      const codes = toCodes(g);
      if (codes.length > 0) clauses.push(codes);
    }
    if (clauses.length > 0) result.push(clauses);
  }

  // Final fallback: if nothing was parsed, treat the whole string as one clause
  if (result.length === 0) {
    const codes = text.match(/\d{2}:\d{3}:\d{3}/g) || [];
    if (codes.length > 0) result.push([codes]);
  }
  return result;
}

/**
 * Checks if a course's prerequisites are satisfied by a list of completed courses and AP credits
 */
export function checkPrerequisites(
  course: RutgersCourse,
  completedCourses: RutgersCourse[],
  apCredits: UserAPCredit[] = []
): PrerequisiteCheck {
  const cacheKey = buildPrerequisiteCheckCacheKey(course, completedCourses, apCredits);
  if (prerequisiteCheckCache.has(cacheKey)) {
    return prerequisiteCheckCache.get(cacheKey)!;
  }

  const result: PrerequisiteCheck = {
    course,
    isSatisfied: true,
    missingPrerequisites: [],
    satisfiedPrerequisites: [],
    prerequisites: course.prerequisites,
  };
  
  if (!course.prerequisites) {
    prerequisiteCheckCache.set(cacheKey, result);
    return result;
  }
  
  try {
    // Prefer structured group parsing for complex expressions
    const prereqText = course.prerequisites;
    const groups = parsePrerequisiteGroups(prereqText);
    if (groups.length === 0) {
      prerequisiteCheckCache.set(cacheKey, result);
      return result;
    }

    // Convert AP credits to course equivalents
    const apCourseEquivalents = convertAPCreditsToCourses(apCredits);
    // Combine completed courses with AP credit equivalents
    const allCompletedCourses = [...completedCourses, ...apCourseEquivalents];

    const hasCourse = (code: string): boolean => {
      if (code.startsWith('>=')) {
        const raw = code.slice(2);
        return checkSingleRequirement({ type: 'credit', value: raw, operator: 'GREATER_THAN' }, allCompletedCourses, prereqText);
      }
      // CS 111/121 and 112/122 are interchangeable prerequisites across the Spring 2027 transition.
      return [code, ...equivalentCourseCodes(code)].some(value =>
        checkSingleRequirement({ type: 'course', value }, allCompletedCourses, prereqText));
    };

    let anyGroupSatisfied = false;
    for (const groupClauses of groups) {
      // Group is satisfied if every clause has at least one course present
      const groupSatisfied = groupClauses.every(clauseCodes =>
        clauseCodes.some(code => hasCourse(code))
      );
      const display = groupClauses
        .map(clause => clause.join(' OR '))
        .join(' AND ');
      if (groupSatisfied) {
        anyGroupSatisfied = true;
        result.satisfiedPrerequisites.push(display);
      } else {
        result.missingPrerequisites.push(display);
      }
    }
    result.isSatisfied = anyGroupSatisfied || groups.length === 0;

  } catch (error) {
    result.error = `Error parsing prerequisites: ${error}`;
    result.isSatisfied = false;
  }
  
  prerequisiteCheckCache.set(cacheKey, result);
  return result;
}

/**
 * Checks if a single prerequisite requirement is satisfied
 */
function checkSingleRequirement(
  requirement: PrerequisiteRequirement,
  completedCourses: RutgersCourse[],
  prerequisites: string
): boolean {
  switch (requirement.type) {
    case 'course':
      // Normalize requirement value to baseId (remove colons and non-digits)
      {
        const normalized = requirement.value.replace(/[^0-9]/g, '');

        const foundCourse = completedCourses.find(course => {
          const completedNormalized = course.baseId.replace(/[^0-9]/g, '');
          return completedNormalized === normalized;
        });

        if (prerequisites.includes("Any Course")) {
          debugPrerequisites("EQUAL or GREATER THAN");
          // extract the department and number from the requirement.value
          // get the class from between {}
          const classNumber = requirement.value.match(/\{(.*)\}/)?.[1];
          const department = requirement.value.split(':')[1];
          const courseNumber = requirement.value.split(':')[2];
          debugPrerequisites("classNumber", classNumber, "department", department, "courseNumber", courseNumber);
          return completedCourses.some(course => {
            return course.department === department && course.class_number >= courseNumber;
          });
        }
      
        if (foundCourse) {
          debugPrerequisites(` -> Requirement satisfied by completed course: ${foundCourse.baseId}`);
          return true;
        } else {
          debugPrerequisites(` -> Requirement NOT satisfied. Checked against:`, completedCourses.map(c => c.baseId));
          return false;
        }
      }
      
    case 'credit':
      // For credit requirements, check if any course in the same department
      // has a course number >= the target (handles "Any Course EQUAL or GREATER Than").
      {
        let subject = '';
        let num = '';
        const m = requirement.value.match(/^(\d{2}):(\d{3}):(\d{3})$/);
        if (m) {
          subject = m[2];
          num = m[3];
        } else {
          // Fallback: digits only, like 01640112
          const digits = requirement.value.replace(/[^0-9]/g, '');
          if (digits.length >= 8) {
            subject = digits.slice(2, 5);
            num = digits.slice(5);
          }
        }
        const targetNum = parseInt(num, 10);
        if (!subject || Number.isNaN(targetNum)) return false;

        debugPrerequisites(`Checking 'EQUAL or GREATER THAN' prerequisite: Subject >= ${subject}, Number >= ${targetNum}`);

        return completedCourses.some(course => {
          const courseSubject = course.department?.split(':')[1];
          if (courseSubject !== subject) return false;
          
          const courseNumInt = parseInt(String(course.class_number).replace(/[^0-9]/g, ''), 10);
          if (Number.isNaN(courseNumInt)) return false;

          const isSatisfied = courseNumInt >= targetNum;
          if (isSatisfied) {
            debugPrerequisites(` -> Requirement satisfied by ${course.department}:${course.class_number}`);
          }
          return isSatisfied;
        });
      }
      
    case 'department':
      return completedCourses.some(course => 
        course.department === requirement.value
      );
      
    default:
      return false;
  }
}

/**
 * Gets all courses that have prerequisites
 */
export function getCoursesWithPrerequisites(courses: RutgersCourse[]): RutgersCourse[] {
  // Only one representative per baseId
  const seen = new Set<string>();
  const out: RutgersCourse[] = [];
  for (const c of courses) {
    if (!c.prerequisites || c.prerequisites.trim() === '') continue;
    if (seen.has(c.baseId)) continue;
    seen.add(c.baseId);
    out.push(c);
  }
  return out;
}

/**
 * Gets all prerequisite courses for a given course
 */
export function getPrerequisiteCourses(course: RutgersCourse): string[] {
  if (!course.prerequisites) return [];

  const groups = parsePrerequisiteGroups(course.prerequisites);
  const codes = new Set<string>();
  groups.forEach(group => {
    group.forEach(clause => {
      clause.forEach(code => {
        if (!code.startsWith('>=')) {
          codes.add(code);
        }
      });
    });
  });

  if (codes.size > 0) return Array.from(codes);

  const requirements = parsePrerequisites(course.prerequisites);
  return requirements
    .filter(req => req.type === 'course')
    .map(req => req.value);
}

export function getPrerequisiteCourseOptions(course: RutgersCourse): string[][][] {
  if (!course.prerequisites) return [];
  return parsePrerequisiteGroups(course.prerequisites)
    .map(group => group.map(clause => clause.filter(code => !code.startsWith('>='))))
    .filter(group => group.some(clause => clause.length > 0));
}

/**
 * Checks if a course can be taken based on completed courses and AP credits
 */
export function canTakeCourse(
  course: RutgersCourse,
  completedCourses: RutgersCourse[],
  apCredits: UserAPCredit[] = []
): boolean {
  const check = checkPrerequisites(course, completedCourses, apCredits);
  return check.isSatisfied;
}

/** Returns a cleaned, human-readable prerequisite string for UI display */
export function formatPrerequisiteText(text?: string): string | undefined {
  if (!text) return undefined;
  return text
    .replace(/<\/?em>/g, '')
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/**
 * Recursively finds all prerequisite courses for a given course.
 * Returns them in a topological order (prerequisite before dependent).
 */
export function getRecursivePrerequisites(
  courseCode: string,
  allCourses: RutgersCourse[],
  seen: Set<string> = new Set()
): string[] {
  const norm = (c: string) => c.replace(/[^0-9]/g, '');
  const targetNorm = norm(courseCode);
  
  if (seen.has(targetNorm)) return [];
  seen.add(targetNorm);

  const course = allCourses.find(c => norm(c.baseId) === targetNorm);
  if (!course || !course.prerequisites) return [];

  const prereqCodes = getPrerequisiteCourses(course);
  const result: string[] = [];

  for (const pCode of prereqCodes) {
    const recursivePrereqs = getRecursivePrerequisites(pCode, allCourses, seen);
    result.push(...recursivePrereqs);
    if (!result.includes(pCode)) {
      result.push(pCode);
    }
  }

  return result;
}
